"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, ThermometerSnowflake, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-20 lg:py-24 border-t border-border">
      {/* Structural visual backdrop accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-40">
        <div className="h-[320px] w-[640px] rounded-full bg-accent/40 blur-3xl" />
      </div>

      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-border bg-card p-6 sm:p-12 md:p-16 text-center shadow-sm">
          {/* Status Indicator Pill */}
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/20 px-3.5 py-1 text-xs font-semibold text-accent-foreground mb-6">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span>Southern California & Las Vegas-Area Service</span>
          </div>

          {/* Heading */}
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Find the right trailer for your operation
          </h2>

          {/* Subtext */}
          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            Request a quote for mobile refrigerated or freezer trailer rental availability in Southern California or our listed Las Vegas-area communities.
          </p>

          {/* Key Quick Reassurances */}
          <div className="mx-auto mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-foreground/80">
            <div className="inline-flex items-center gap-2">
              <ThermometerSnowflake className="size-4 text-primary shrink-0" />
              <span>Trailer options include 6×8, 7×12, and 7×16 towable units, plus a 53-foot reefer trailer.</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <Clock className="size-4 text-primary shrink-0" />
              <span>Rental dates, availability, pricing, and terms are confirmed with each quote.</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>Verified small-trailer specifications are shown above. The 53-foot reefer&apos;s specifications are confirmed individually.</span>
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto sm:min-w-[220px] bg-primary text-primary-foreground font-semibold px-8 py-3.5 text-base shadow-sm hover:bg-primary/90 transition-colors">
              <Link to="/ContactRequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                <span>Request a Quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}